"use client";

import { Star, Users, Clock, BarChart3, CheckCircle2 } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { Card } from "@/components/ui/card";
import { Tag } from "@/components/tag";

export interface QuizCardData {
  id: string;
  title: string;
  slug?: string;
  category?: string;
  subCategory?: string;
  level: string;
  totalQuestions: number;
  duration: string;
  rating: number;
  totalReviews: number;
  totalParticipants: number;
  image: string;
  price: number;
  originalPrice?: number;
  isPurchased?: boolean;
}

interface QuizCardProps {
  quiz: QuizCardData;
  index: number;
  showCategory?: boolean;
  /** Base path for exam detail link. Defaults to "/ujian" (public). Use "/ujian" for logged-in user pages. */
  basePath?: string;
}

export const QuizCard = ({
  quiz,
  index,
  showCategory = true,
  basePath = "/ujian",
}: QuizCardProps) => {
  const hasDiscount = quiz.originalPrice && quiz.originalPrice > quiz.price;

  // Kartu = elemen tetap (BUKAN komponen inline). Sebelumnya `Wrapper` dibuat
  // ulang tiap render → React unmount+mount ulang seluruh kartu setiap kali
  // parent re-render (purchasedIds/session/scroll) → animasi masuk (opacity 0→1)
  // replay terus = kartu "blink". Kini wrapper div dipertahankan; Link
  // membungkus secara kondisional tanpa mengganti tipe komponen. Animasi masuk
  // = CSS `.anim-card` (delay stagger via inline style) — framer-motion
  // dikeluarkan dari graph landing (audit 2026-09-24-landing-performance-audit).
  const card = (
    <div
      className="anim-card cursor-pointer"
      style={{ animationDelay: `${0.04 * (index % 6)}s` }}
    >
      {/* py-0 gap-0 — desain kartu ini image-bleed (thumbnail flush ke tepi).
          Stock shadcn v4 Card default py-6 gap-6; tanpa override ini thumbnail
          terdorong 24px (regresi pasca migrasi card.tsx ke stock v4). twMerge
          memastikan override konsumen menang atas base. */}
      <Card className="group hover:border-primary/20 flex h-full flex-col gap-0 overflow-hidden py-0 transition-all duration-200 hover:-translate-y-1 active:scale-[0.98]">
        {/* Cover image */}
        <div className="relative h-[110px] overflow-hidden sm:h-[150px]">
          {/* `image` bisa string kosong (exam tanpa thumbnail) — Image dgn
              src="" melempar error & memicu fetch ulang halaman. Render
              placeholder gradient; geometry tetap. */}
          {quiz.image ? (
            <Image
              src={quiz.image}
              alt={quiz.title}
              fill
              sizes="(max-width: 640px) 100vw, 33vw"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="from-primary/15 to-primary/5 h-full w-full bg-gradient-to-br" />
          )}
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/35 to-transparent" />
          <div className="bg-card/[0.18] pointer-events-none absolute -top-4 -right-4 h-[70px] w-[70px] rounded-full" />
          <div className="bg-card/[0.18] pointer-events-none absolute -bottom-3 left-4 h-[45px] w-[45px] rounded-full" />

          {/* Top-left: category & sub-category badges — inline (sejajar), bukan
            atas-bawah. Badge kategori disembunyikan jika sub-kategori sudah
            memuatnya (mis. "CPNS" + "CPNS Kemenkumham" → cukup tampil sub-nya). */}
          {showCategory && (quiz.category || quiz.subCategory) && (
            <div
              className={`absolute top-2.5 left-2.5 flex items-center gap-1 ${quiz.isPurchased || hasDiscount ? "max-w-[calc(100%-110px)]" : "max-w-[calc(100%-20px)]"}`}
            >
              {quiz.category &&
                !(
                  quiz.subCategory &&
                  quiz.subCategory.toLowerCase().includes(quiz.category.toLowerCase())
                ) && (
                  <span className="bg-card/90 text-foreground text-2xs shrink-0 rounded-lg px-2.5 py-0.5 font-bold whitespace-nowrap">
                    {quiz.category}
                  </span>
                )}
              {quiz.subCategory && quiz.subCategory !== quiz.category && (
                <span className="bg-primary/85 text-primary-foreground text-3xs min-w-0 truncate rounded-lg px-2.5 py-0.5 font-semibold">
                  {quiz.subCategory}
                </span>
              )}
            </div>
          )}

          {/* Bottom-left: level badge */}
          {quiz.level && (
            <span className="bg-primary text-primary-foreground text-2xs absolute bottom-2 left-2.5 rounded px-2 py-0.5 font-semibold">
              {quiz.level}
            </span>
          )}

          {/* Top-right: purchased / discount badge */}
          {quiz.isPurchased ? (
            <span className="bg-primary text-primary-foreground text-2xs shadow-soft-sm absolute top-2.5 right-2.5 flex items-center gap-1 rounded-lg px-2.5 py-0.5 font-extrabold [--soft-shadow-color:var(--primary)]">
              <CheckCircle2 size={11} />
              Sudah Dibeli
            </span>
          ) : hasDiscount ? (
            <span className="bg-destructive text-primary-foreground text-2xs shadow-soft-sm absolute top-2.5 right-2.5 rounded-lg px-2.5 py-0.5 font-extrabold [--soft-shadow-color:var(--destructive)]">
              -
              {Math.round(
                ((quiz.originalPrice! - quiz.price) / quiz.originalPrice!) * 100,
              )}
              %
            </span>
          ) : null}
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col p-2.5 sm:p-3.5">
          <h3 className="text-foreground group-hover:text-primary mb-1.5 line-clamp-2 text-base leading-snug font-bold sm:mb-2">
            {quiz.title}
          </h3>

          <div className="text-foreground/45 text-3xs mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 sm:mb-2.5 sm:gap-3 sm:text-xs">
            <span className="flex items-center gap-1">
              <Clock size={10} className="sm:size-3" />
              {quiz.duration}
            </span>
            <span className="flex items-center gap-1">
              <BarChart3 size={10} className="sm:size-3" />
              {quiz.totalQuestions} soal
            </span>
          </div>

          {/* Price / Purchased */}
          <div className="mb-2 flex flex-wrap items-center gap-1.5 sm:mb-2.5 sm:gap-2">
            {quiz.price === 0 ? (
              <Tag tone="primary" size="sm" className="font-bold sm:text-xs">
                Gratis
              </Tag>
            ) : (
              <>
                <span className="text-primary text-base font-extrabold">
                  Rp{quiz.price.toLocaleString("id-ID")}
                </span>
                {hasDiscount && (
                  <span className="text-foreground/60 sm:text-2xs text-3xs line-through">
                    Rp{quiz.originalPrice!.toLocaleString("id-ID")}
                  </span>
                )}
              </>
            )}
            {quiz.isPurchased && (
              <Tag tone="primary" size="sm" className="font-bold sm:text-xs">
                <CheckCircle2 size={10} /> Dibeli
              </Tag>
            )}
          </div>

          <div className="flex-1" />

          {/* Footer: rating + participants */}
          <div className="border-muted-foreground/30 flex items-center justify-between border-t pt-2 sm:pt-2.5">
            <div className="flex items-center gap-0.5 sm:gap-1">
              <Star size={10} className="fill-secondary text-secondary sm:size-3" />
              <span className="text-foreground/60 text-3xs font-semibold sm:text-xs">
                {quiz.rating}
              </span>
              <span className="text-foreground/60 text-3xs">({quiz.totalReviews})</span>
            </div>
            <span className="text-foreground/60 text-3xs sm:text-2xs flex items-center gap-0.5 sm:gap-1">
              <Users size={10} className="sm:size-[11px]" />
              {quiz.totalParticipants.toLocaleString("id-ID")}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );

  return quiz.slug ? <Link href={`${basePath}/${quiz.slug}`}>{card}</Link> : card;
};
